import React from 'react'
import { Box, Typography, Chip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  Calendar,
  Building2,
  MapPin,
  Hash,
  ArrowRight,
} from 'lucide-react'
import { useSearchTokens } from '../../SearchScreen/searchDesignTokens'

// Color map for announcement types
const TYPE_COLORS = {
  constitution: '#22c55e',
  dissolution: '#ef4444',
  modification_du_capital: '#f59e0b',
  cession_de_parts: '#8b5cf6',
  changement_de_gerant: '#3b82f6',
  transfert_de_siege: '#06b6d4',
  fusion: '#ec4899',
  transformation: '#14b8a6',
  prorogation: '#64748b',
  dissolution_anticipee: '#dc2626',
  modification_statuts: '#f97316',
}

const AnnonceCard = ({ annonce }) => {
  const { t } = useTranslation()
  const dk = useSearchTokens()
  const navigate = useNavigate()

  const typeColor = TYPE_COLORS[annonce.type_annonce] || dk.gold

  const formatDate = (dateStr) => {
    if (!dateStr) return null
    try {
      const date = new Date(dateStr)
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    } catch {
      return dateStr
    }
  }

  const handleCardClick = () => {
    navigate(`/legal-notice/annonce/${encodeURIComponent(annonce.annonce_id)}`)
  }

  const handleCompanyClick = (e) => {
    e.stopPropagation()
    if (annonce.registre_commerce) {
      const params = new URLSearchParams()
      if (annonce.ville_rc) params.set('ville_rc', annonce.ville_rc)
      navigate(
        `/legal-notice/company/${encodeURIComponent(annonce.registre_commerce)}${
          params.toString() ? `?${params.toString()}` : ''
        }`
      )
    }
  }

  const formatCapital = (capital, devise) => {
    if (!capital) return null
    const formatted = Number(capital).toLocaleString('fr-FR')
    return `${formatted} ${devise || 'MAD'}`
  }

  return (
    <Box
      data-tour="annonce-card"
      onClick={handleCardClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        p: '14px 16px',
        cursor: 'pointer',
        backgroundColor: dk.surface,
        border: `1px solid ${dk.border}`,
        borderRadius: '10px',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: dk.hover,
          borderColor: dk.borderHover,
          boxShadow: dk.shadowCard,
        },
      }}
    >
      {/* Top row: Type badge + date */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Chip
          label={t(`type_${annonce.type_annonce}`, annonce.type_annonce)}
          size="small"
          sx={{
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 600,
            color: typeColor,
            backgroundColor: `${typeColor}12`,
            border: `1px solid ${typeColor}35`,
            '& .MuiChip-label': { px: 1 },
          }}
        />
        {annonce.date_publication && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Calendar size={12} color={dk.disabled} />
            <Typography
              sx={{ fontSize: '0.72rem', color: dk.text3, fontWeight: 500 }}
            >
              {formatDate(annonce.date_publication)}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Company name */}
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: '0.9rem',
          lineHeight: 1.4,
          color: dk.text1,
          mb: 0.5,
          width: '100%',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {annonce.denomination || t('unnamed_company', 'Société non nommée')}
      </Typography>

      {/* Forme juridique + capital */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        {annonce.forme_juridique && (
          <Typography
            sx={{
              fontSize: '0.72rem',
              fontWeight: 500,
              color: dk.gold,
              backgroundColor: dk.goldSub,
              px: 0.8,
              py: 0.2,
              borderRadius: '4px',
              border: `1px solid ${dk.goldBdr}`,
            }}
          >
            {t(`forme_${annonce.forme_juridique}`, annonce.forme_juridique)}
          </Typography>
        )}
        {annonce.capital_social && (
          <Typography
            sx={{
              fontSize: '0.72rem',
              fontWeight: 500,
              color: dk.text2,
              backgroundColor: dk.chipBg,
              px: 0.8,
              py: 0.2,
              borderRadius: '4px',
            }}
          >
            {formatCapital(annonce.capital_social, annonce.devise)}
          </Typography>
        )}
      </Box>

      {/* Footer: RC, ville, company link */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pt: 0.8,
          borderTop: `1px solid ${dk.borderLight}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {annonce.registre_commerce && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <Hash size={11} color={dk.disabled} />
              <Typography
                sx={{ fontSize: '0.72rem', color: dk.text3, fontWeight: 500 }}
              >
                RC: {annonce.registre_commerce}
              </Typography>
            </Box>
          )}
          {annonce.ville && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <MapPin size={11} color={dk.disabled} />
              <Typography
                sx={{ fontSize: '0.72rem', color: dk.text3, fontWeight: 500 }}
              >
                {annonce.ville}
              </Typography>
            </Box>
          )}
        </Box>

        {annonce.registre_commerce && (
          <Box
            onClick={handleCompanyClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.4,
              cursor: 'pointer',
              '&:hover': {
                '& .company-link': { textDecoration: 'underline' },
              },
            }}
          >
            <Building2 size={12} color={dk.gold} />
            <Typography
              className="company-link"
              sx={{
                fontSize: '0.7rem',
                color: dk.gold,
                fontWeight: 500,
              }}
            >
              {t('view_company', 'Fiche entreprise')}
            </Typography>
            <ArrowRight size={10} color={dk.gold} />
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default AnnonceCard
